// @flow
import * as React from 'react';
import moment from 'moment';

import { RESORT_GRID } from 'Client/cms/toolkit/constants';
import * as configQueries from 'Views/configQueries';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { momentToApiDate } from 'Utils/date/apiDate';
import makeResortThumbnailQuery from 'Utils/resort-thumbnail-query/resortThumbnailQuery';
import type { ThumbnailProduct } from 'Utils/resort-thumbnail-query/resortThumbnailQuery';
import {
  LINE_COMMON_PRODUCT_GRID_IDS,
  LINE_COMMON_PRODUCT_GRID_IDS_WITH_OFFER,
  LINE_COMMON_PRODUCT_GRID_PRODUCTS,
  LINE_COMMON_PRODUCT_GRID_THUMBNAIL_TRADS,
} from 'Utils/request/requestDictionary';
import { grid } from 'Components/widgets/product/Product.utils';

type ProductIds = Array<string>;

type GQLData = {
  productIds: ProductIds,
  products: Array<ThumbnailProduct>,
  offer: Offer,
  config: *,
};

type GQLProps = {
  productIds?: ProductIds,
  products?: Array<ThumbnailProduct>,
  offer?: Offer,
  config: *,
};

export type CMSProps = {
  moreResultsColor?: string,
  moreResultsLabel?: string,
  moreResultsButtonLabel?: string,
  tourDurationPlaceholder?: string,
  cruiseTimingPlaceholder?: string,
  cruiseDurationPlaceholder?: string,
  dateFormat?: string,
  resortIds?: Array<string>,
  offerId?: string,
  departureDateStart?: string,
  departureDateEnd?: string,
  duration?: number,
  departureCity?: string,
  unfoldList?: boolean,
};

export type ProductsGridDescriptorProps = {
  ...$Exact<CMSProps>,
  ...$Exact<GQLData>,
  ...$Exact<GQLProps>,
};

export type FetchProductsParamsType = {
  appContext: AppContext,
  productIds: ProductIds,
  props: CMSProps,
  offerId?: string,
};

type OfferWithProductIds = {
  resortIds: Array<string>,
  offer?: Offer,
};

function getThumbnailDatesArgs(props: CMSProps) {
  const today = moment();
  const start = moment(props.departureDateStart || null); // moment(undefined) === today, moment(null) === invalid date. Dat's why
  const end = moment(props.departureDateEnd || null);
  if (!start.isValid() || !end.isValid() || end.isSameOrBefore(today)) {
    return {};
  }
  return {
    duration: props.duration,
    departureCity: props.departureCity,
    dateStart: momentToApiDate(start.isBefore(today) ? today : start),
    dateEnd: momentToApiDate(end),
  };
}
// Exported for test purpose only
export function buildProductsQuery({
  appContext,
  productIds,
  offerId,
  props,
}: FetchProductsParamsType): string {
  const { cmsContent: content = '', cmsRelease: release = '' } = appContext;

  const thumbnailArgs = {
    content,
    release,
    offerId,
    tourDurationPlaceholder: props.tourDurationPlaceholder,
    cruiseTimingPlaceholder: props.cruiseTimingPlaceholder,
    cruiseDurationPlaceholder: props.cruiseDurationPlaceholder,
    dateFormat: props.dateFormat,
    ...getThumbnailDatesArgs(props),
  };

  return `query {
    products : resorts(resortIds : ${JSON.stringify(productIds)}) {
      ${makeResortThumbnailQuery(thumbnailArgs)}
    }
    config(content : "${content}" release : "${release}") {
      ${configQueries.resort(appContext)}
      ${configQueries.price()}
      common { close thumbnailCTA thumbnailTourCTA openingSoon closed }
    }
  }`;
}

export function buildOfferWithProductIdsQuery(productIds: ProductIds, offerId: string): string {
  return `query {
    offer(offerId : "${offerId}") {
      id
      name
      seoName
      color
      resortIds(resortIds: ${JSON.stringify(productIds)})
    }
  }`;
}

export function fetchProducts({
  appContext,
  productIds,
  offerId,
  props,
}: FetchProductsParamsType): Promise<*> {
  const query = buildProductsQuery({ appContext, productIds, offerId, props });
  return graphQLClient.runQuery(query, appContext, {
    requestName: LINE_COMMON_PRODUCT_GRID_PRODUCTS,
    strictErrors: false,
  });
}

export function buildProductIdsQuery(productIds: ProductIds) {
  return `query {
    products : resorts(resortIds : ${JSON.stringify(productIds)}) {
      id
    }
  }`;
}

function getThumbnailCMSTrads(appContext) {
  return graphQLClient.runQuery(
    `query {
      config {
        thumbnails {
          cruiseDurationPlaceholder
          tourDurationPlaceholder
          cruiseTimingPlaceholder
        }
      }
    }`,
    appContext,
    { requestName: LINE_COMMON_PRODUCT_GRID_THUMBNAIL_TRADS },
  );
}

async function fetchOfferWithProductIds(
  appContext: AppContext,
  productIds: ProductIds,
  offerId: string,
): Promise<OfferWithProductIds> {
  const query = buildOfferWithProductIdsQuery(productIds, offerId);

  const {
    offer: { resortIds, ...rest },
  } = await graphQLClient.runQuery(query, appContext, {
    requestName: LINE_COMMON_PRODUCT_GRID_IDS_WITH_OFFER,
    strictErrors: false,
  });

  return {
    resortIds,
    offer: rest,
  };
}

async function fetchProductIds(
  appContext: AppContext,
  productIds: ProductIds,
): Promise<OfferWithProductIds> {
  const query = buildProductIdsQuery(productIds);

  const { products } = await graphQLClient.runQuery(query, appContext, {
    requestName: LINE_COMMON_PRODUCT_GRID_IDS,
    strictErrors: false,
  });
  return { resortIds: products.map(({ id }) => id) };
}

export const productsGridResolver = {
  name: RESORT_GRID,
  async resolve(appContext: AppContext, props: CMSProps): Promise<GQLProps | null> {
    const { resortIds: productIds = [], offerId } = props;
    const fetchOfferAndProductIds = await (offerId
      ? fetchOfferWithProductIds(appContext, productIds, offerId).catch(() =>
          fetchProductIds(appContext, productIds),
        )
      : fetchProductIds(appContext, productIds));

    const { offer = {}, resortIds = [] } = fetchOfferAndProductIds;
    const sortedExistingProductIds = productIds.filter(id => resortIds.indexOf(id) !== -1);
    const subsetProductIds = props.unfoldList
      ? sortedExistingProductIds
      : sortedExistingProductIds.slice(0, grid.desktop);

    if (!subsetProductIds || subsetProductIds.length === 0) {
      // Silently exit if given product IDs don't actually exist.
      return null;
    }
    const { config: thumbnailCMSTrads } = await getThumbnailCMSTrads(appContext);

    const { products = [], config } = await fetchProducts({
      appContext,
      productIds: subsetProductIds,
      offerId: offer.id,
      props: { ...props, ...thumbnailCMSTrads.thumbnails },
    });

    return {
      productIds: sortedExistingProductIds,
      products: sortedExistingProductIds.reduce((acc, next) => {
        const product = products.find(({ id }) => id === next);

        if (product) {
          return [...acc, product];
        }

        return acc;
      }, []),
      config,
      offer,
    };
  },
};
